import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AdminService,
  AppAuthServiceService,
  CasesService,
  CatalogService,
  FormHooksService,
  FormValuesService,
  SpinnerService,
  ToastService,
  UserPreferencesService,
} from '@core';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Case, caseIdentifier, CatalogItem, FormSection, formValuePicker, RegisteredCase, UserSettings } from '@shared';
import { finalize, forkJoin, map, Observable } from 'rxjs';
import quickFadeIn from 'src/app/shared/animations/quick-fadeIn.animation';

@Component({
    selector: 'ups-entry-form',
    templateUrl: './entry-form.component.html',
    styleUrls: ['./entry-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    animations: [quickFadeIn],
    standalone: false
})
export class EntryFormComponent implements OnInit {
  public sections: FormSection[] = [];
  public form: FormGroup = new FormGroup({});
  public options: FormlyFormOptions = {
    formState: {
      state: {
        hasStates: false,
      },
      stateAssistance: {
        hasStates: false,
      },
    },
  };
  public fields: FormlyFieldConfig[] = [];
  public model: any = {
    //contactRequired: true,
    contact: 'inquirer',
  };
  public ready: boolean = false;
  public showErrorSummaryModal: boolean = false;
  public caseId: string = '';

  public showVoidModal: boolean = false;
  public reasonToVoidForm: FormGroup = new FormGroup({
    reason: new FormControl(null, [Validators.required]),
  });

  public navigationHistory: boolean = false;
  public hide_expression = true;
  private preferences: UserSettings = { language: 'en' };
  private defaultShipmentIdentifier = '';
  private businessUnitPreference = '';
  private accountId: string;
  private registeredCase: RegisteredCase = { value: {} as Case, lastUpdate: '' };

  public showConfirmModal: boolean = false;

  constructor(
    private readonly spinnerService: SpinnerService,
    private catalogService: CatalogService,
    private formValuesService: FormValuesService,
    private formHooksService: FormHooksService,
    private casesService: CasesService,
    private router: Router,
    private userPreferences: UserPreferencesService,
    private appAuthService: AppAuthServiceService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private location: Location,
    private adminService: AdminService
  ) {
    this.accountId = this.appAuthService.account.homeAccountId || '';
    this.caseId = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit() {
    if (this.caseId.length) {
      this.init();
    } else {
      this.adminService
        .checkSystemStatus()
        .pipe(finalize(() => this.spinnerService.hide()))
        .subscribe((systemStatus) => {
          if (!systemStatus.enabled) {
            this.router.navigate(['/home']);
          } else {
            this.init();
          }
        });
    }
  }

  public voidCase(): void {
    if (!this.reasonToVoidForm.valid) {
      return;
    }
    this.spinnerService.show();
    this.showVoidModal = false;
    this.casesService
      .voidCase(this.caseId, this.reasonToVoidForm.get('reason')?.value)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
          this.reasonToVoidForm.reset();
        })
      )
      .subscribe({
        next: () => {
          if (!this.navigationHistory) {
            this.router.navigate([`/case/success/${this.caseId}`]);
          } else {
            this.back();
          }
        },
        error: (response) => {
          if (response.error) {
            if (response.error.error.summary) {
              this.toast.show(response.error.error.summary.join(', '), 'error');
            } else {
              this.toast.show(response.error.error.error, 'error');
            }
          } else {
            this.toast.show('Error', 'error');
          }
        },
      });
  }

  public getFormValidationErrors(): {
    key?: string;
    label?: string;
    section?: string;
    error?: boolean;
  }[] {
    const fields: any[] = [];
    this.fields.forEach((section: FormlyFieldConfig) => {
      if (section.className && section.templateOptions && section.templateOptions.label) {
        fields.push({ section: section.templateOptions.label });
      }
      if (section.fieldGroup) {
        section.fieldGroup.forEach((field) => {
          if (field.key) {
            fields.push({ key: field.key?.toString(), label: field.templateOptions?.label?.replace(':', '') });
          }
        });
      }
    });
    Object.keys(this.form.controls).forEach((key) => {
      const controlErrors: ValidationErrors | null | undefined = this.form.get(key)?.errors;
      if (controlErrors) {
        Object.keys(controlErrors).forEach((keyError) => {
          fields.forEach((field) => {
            if (field.key === key) {
              field.error = true;
            }
          });
        });
      }
    });
    return fields;
  }

  public onSubmit(model: any): void {
    if (!this.form.valid) {
      this.showErrorSummaryModal = true;
      return;
    }

    const FormattedModel: Case = this.casesService.formatBody(model);
    this.registeredCase.value = FormattedModel;

    let serviceCall: Observable<caseIdentifier> = this.caseId.length
      ? this.casesService.updateCase(this.registeredCase, this.caseId)
      : this.casesService.createCase(FormattedModel);

    this.spinnerService.show();

    serviceCall.pipe(finalize(() => this.spinnerService.hide())).subscribe({
      next: (response) => {
        if (!this.navigationHistory) {
          this.router.navigate([`/case/success/${response.id ? response.id : this.caseId}`]);
        } else {
          this.back();
        }
      },
      error: (response) => {
        if (response.error) {
          if (response.error.error.summary) {
            this.toast.show(response.error.error.summary.join(', '), 'error');
          } else {
            this.toast.show(response.error.error.error, 'error');
          }
        } else {
          this.toast.show('Error', 'error');
        }
      },
    });
  }

  private init(): void {
    const historyParam = this.route.snapshot.queryParams['navigationHistory'];
    this.navigationHistory = historyParam ? (historyParam === 'true' ? true : false) : false;

    this.spinnerService.show();
    this.userPreferences.getUserPreferences(this.accountId).subscribe((preferences) => {
      if (!preferences.error) {
        this.preferences = preferences;
      }
      this.catalogService.setLanguage(this.preferences.language);
      if (this.caseId.length) {
        this.casesService.getCaseById(this.caseId).subscribe((registeredCase: RegisteredCase) => {
          this.registeredCase.lastUpdate = registeredCase.lastUpdate;
          this.model = this.casesService.parseRegisteredCase(registeredCase);
          this.model.premierShipmentNotes = this.model.premierShipment ? true : false;
          this.businessUnitPreference = this.model.businessUnit || '';
          this.defaultShipmentIdentifier = this.preferences.shipmentIdentifier || '';
          this.loadFormOptions();
        });
      } else {
        this.loadFormOptions();
      }
    });
  }

  private loadFormOptions(): void {
    this.getFormValues([
      this.catalogService.formSchema(this.preferences.language).pipe(
        map((schema) => this.sortSections(schema.value)),
        map((schema) => this.buildForm(schema))
      ),
      this.catalogService.inquiryCountry(),
      this.catalogService.contactType(),
      this.catalogService.country(),
      this.catalogService.preferredCommunicationChannel(),
      this.catalogService.language(),
      this.catalogService.shipmentIdentifier(),
      this.catalogService.priority(),
      this.catalogService.receivedFrom(),
    ]);
  }

  private sortSections(sections: FormSection[]): FormSection[] {
    return [...sections.sort((a, b) => (a.order < b.order ? -1 : 0))];
  }

  private buildForm(sections: FormSection[]): FormlyFieldConfig[] {
    const formFields: any[] = [];
    sections.forEach((item) => {
      item.fields.forEach((field) => {
        if (field.fieldGroup && field.fieldGroup.length) {
          field.fieldGroup.forEach((groupItem) => {
            switch (groupItem.key) {
              case 'state':
                groupItem.hideExpression = (_model, formState) => !formState.state?.hasStates;
                groupItem.expressionProperties = {
                  'templateOptions.required': (_model, formState) => formState.state?.hasStates || false,
                };
                break;
              case 'stateAssistance':
                groupItem.hideExpression = (_model, formState) => !formState.stateAssistance?.hasStates;
                groupItem.expressionProperties = {
                  'templateOptions.required': (_model, formState) => formState.stateAssistance?.hasStates || false,
                };
                break;
              case 'mobilePhone':
                groupItem.hideExpression = (model) => model.isPrimary;
                break;
              case 'email':
                groupItem.expressionProperties = {
                  'templateOptions.required': (model) => model.contactMethod === 'Email',
                };
                break;
              case 'contact':
                groupItem.hideExpression = false;
                break;
              case 'premierShipment' :
                groupItem.hideExpression = (model) => { 
                  // model.premierShipment ='';
                  return !model.premierShipmentNotes;
                }
                break;
              case 'emailAssistance':
                groupItem.expressionProperties = {
                  'templateOptions.required': (model) => model.contactMethodAssistance === 'Email',
                };
                break;
              case 'countryAssistance':
                if (groupItem.templateOptions) {
                  groupItem.templateOptions['disabled'] = true;
                } else {
                  groupItem.templateOptions = {
                    disabled: true,
                  };
                }
                break;
              case 'country':
                groupItem.expressionProperties = {
                  'templateOptions.disabled': (model) => model.sameAsCaller,
                };
                break;
              case 'sameAsCaller': {
                groupItem.hideExpression = (model) => {
                  return model.inquirerCountry != model.country;
                };
                break;
              }
              case 'shipmentIdentifierValues': {
                const shipmentIdentifierField: FormlyFieldConfig = groupItem.templateOptions?.['fields'].find(
                  (field: FormlyFieldConfig) => field?.key === 'shipmentIdentifier'
                );

                groupItem.expressionProperties = {
                  'templateOptions.required': (model, formState) => !!formState.shipmentIdentifierValues?.required,
                };

                if (groupItem.templateOptions) {
                  groupItem.templateOptions.label = shipmentIdentifierField.templateOptions?.label;
                }

                break;
              }
              case 'accountName': {
                const contactTypes: string[] = ['ups account', 'non-account business', '3rd party auditor'];
                groupItem.expressionProperties = {
                  'templateOptions.required': (model) => {
                    if (model.contactType) {
                      return contactTypes.includes(model.contactType.toLowerCase());
                    } else {
                      return false;
                    }
                  },
                };
                break;
              }
              case 'accountNumber': {
                groupItem.expressionProperties = {
                  'templateOptions.required': (model) => {
                    if (model.contactType) {
                      return model.contactType.toLowerCase() === 'ups account';
                    } else {
                      return false;
                    }
                  },
                };
                break;
              }
            }
          });
        }
        formFields.push(field);
      });
    });
    this.sections = sections;
    return formFields;
  }

  private getFormValues(requests: Observable<any>[]): void {
    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.spinnerService.hide();
          this.ready = true;
        })
      )
      .subscribe((response) => {
        this.initValuesAndHooks(response);
      });
  }

  private initValuesAndHooks([
    formConfig,
    inquiryCountry,
    contactType,
    countries,
    preferredCommunicationChannel,
    language,
    shipmentIdentifier,
    priority,
    receivedFrom,
  ]: any[]): void {
    if (!this.caseId.length) {
      this.loadPreferences(language);
    } else {
      const inquirerCountryId = countries.find((x: CatalogItem) => x.apiName === this.model['inquirerCountry']);
      const country = countries.find((x: CatalogItem) => x.apiName === this.model['country']);
      const contactLanguage = language.find((x: CatalogItem) => x.apiName === this.model['contactLanguageCode']);
      const contactLanguageAssistance = language.find(
        (x: CatalogItem) => x.apiName === this.model['contactLanguageCode']
      );
      this.model = {
        ...this.model,
        contactLanguageCode: contactLanguage?.id,
        contactLanguageAssistanceCode: contactLanguageAssistance?.id,
        inquirerCountryId: inquirerCountryId?.id,
        countryId: country?.id,
      };
    }

    this.formValuesService.init({
      fieldsConfig: formConfig,
      initialize: [
        { key: formValuePicker.inquirerCountry, withValues: inquiryCountry },
        { key: formValuePicker.businessUnit, withValues: [] },
        { key: formValuePicker.contactType, withValues: contactType },
        { key: formValuePicker.contactRoleAssistance, withValues: receivedFrom },
        { key: formValuePicker.country, withValues: countries },
        { key: formValuePicker.state, withValues: [] },
        { key: formValuePicker.contactMethod, withValues: preferredCommunicationChannel },
        { key: formValuePicker.contactLanguage, withValues: language },
        {
          key: formValuePicker.shipmentIdentifierValues,
          withValues: shipmentIdentifier,
          defaultValue: this.defaultShipmentIdentifier,
        },
        { key: formValuePicker.priority, withValues: priority },
        // { key: formValuePicker.premierShipment, withValues: []},
        { key: formValuePicker.countryAssistance, withValues: countries },
        { key: formValuePicker.stateAssistance, withValues: [] },
        { key: formValuePicker.contactMethodAssistance, withValues: preferredCommunicationChannel },
        { key: formValuePicker.contactLanguageAssistance, withValues: language },
        { key: formValuePicker.inquirerRole, withValues: receivedFrom },
        { key: formValuePicker.caseCategory, withValues: [] },
        { key: formValuePicker.caseSubCategory, withValues: [] },
      ],
      whenDone: (updatedConfig) => {
        this.fields = updatedConfig;
      },
    });

    this.formHooksService.init({
      fieldsConfig: this.fields,
      formlyOptions: this.options,
      hooks: [
        {
          fieldKey: formValuePicker.inquirerCountry,
          onChanges: this.formHooksService.businessUnitHook({
            target: formValuePicker.businessUnit,
            stateTarget: formValuePicker.stateAssistance,
            defaultValue: this.businessUnitPreference,
            model: this.model,
          }),
        },
        {
          fieldKey: formValuePicker.businessUnit,
          onChanges: this.formHooksService.categoryHook({ target: formValuePicker.caseCategory }),
        },
        {
          fieldKey: formValuePicker.premierShipmentNotes,   
          onChanges: this.formHooksService.resetpremierShipmentHook({
            target: formValuePicker.premierShipmentNotes,
            form: this.form,
          }),
        },
        {
          fieldKey: formValuePicker.city,
          onChanges: this.formHooksService.copyIfSameAsCallerHook({
            target: formValuePicker.cityAssistance,
            form: this.form,
          }),
        },
        {
          fieldKey: formValuePicker.country,
          onChanges: this.formHooksService.stateProvinceHook({
            target: formValuePicker.state,
            defaultValue: this.model.state,
            model: this.model,
          }),
        },
        {
          fieldKey: formValuePicker.caseCategory,
          onChanges: this.formHooksService.subcategoryHook({ target: formValuePicker.caseSubCategory }),
        },
        {
          fieldKey: formValuePicker.caseSubCategory,
          onChanges: this.formHooksService.shipmentIdentifierHook({ target: formValuePicker.shipmentIdentifierValues }),
        },
        {
          fieldKey: formValuePicker.postalCode,
          onChanges: this.formHooksService.copyIfSameAsCallerHook({
            target: formValuePicker.postalCodeAssistance,
            form: this.form,
          }),
        },
        {
          fieldKey: formValuePicker.state,
          onChanges: this.formHooksService.copyIfSameAsCallerHook({
            target: formValuePicker.stateAssistance,
            form: this.form,
          }),
        },
        {
          fieldKey: formValuePicker.street,
          onChanges: this.formHooksService.copyIfSameAsCallerHook({
            target: formValuePicker.streetAssistance,
            form: this.form,
          }),
        },
        {
          fieldKey: 'isPrimary',
          onChanges: this.formHooksService.isPrimaryHook(),
        },
        {
          fieldKey: 'sameAsCaller',
          onChanges: this.formHooksService.sameAsCallerHook({
            whenDone: (updatedConfig, newValues) => {
              this.fields = updatedConfig;
              this.form.patchValue(newValues);
            },
          }),
        },
      ],
      whenDone: (updatedConfig) => {
        this.fields = updatedConfig;

        if ((this.preferences && this.preferences.inquirerCountryId) || this.caseId.length) {
          const hook = this.formHooksService.businessUnitHook({
            target: formValuePicker.businessUnit,
            stateTarget: formValuePicker.stateAssistance,
            defaultValue: this.businessUnitPreference,
            stateDefault: this.model.stateAssistance,
            model: this.model,
          });

          if (this.caseId.length && this.model.countryId) {
            const stateHook = this.formHooksService.stateProvinceHook({
              target: formValuePicker.state,
              defaultValue: this.model.state,
              model: this.model,
            });

            stateHook({ context: null, field: { key: formValuePicker.country } }, this.model.countryId);
          }

          hook(
            { context: null, field: { key: formValuePicker.inquirerCountry } },
            this.caseId.length ? this.model.inquirerCountryId : this.preferences.inquirerCountryId
          );
        }
      },
    });
  }

  private loadPreferences(languages: CatalogItem[]) {
    const { language, inquirerCountry, businessUnit, shipmentIdentifier } = this.preferences;
    const selectedLanguage = languages.find((x) => x.apiName === language);
    this.model = {
      ...this.model,
      inquirerCountry,
      countryAssistance: inquirerCountry,
      contactLanguage: selectedLanguage?.name,
      contactLanguageCode: selectedLanguage?.id,
      contactLanguageAssistance: selectedLanguage?.name,
      contactLanguageAssistanceCode: selectedLanguage?.id,
    };
    this.businessUnitPreference = businessUnit || '';
    this.defaultShipmentIdentifier = shipmentIdentifier || '';
  }

    public back(): void {
      this.location.back();
    }
  public  openPopup(): void {
    if(this.form.touched) {
      this.showConfirmModal = true;
    }
    else {
      this.back();
    }
  }
  }
